import React, { useMemo } from "react";
import { useBox, usePlane } from "use-cannon";
import { MeshBasicMaterial, MathUtils } from "three";

interface WallProps {
  pos: number[];
  rot: number[];
  scale: number[];
  material: MeshBasicMaterial;
}

const Wall: React.FC<WallProps> = ({ pos, rot, scale, material }) => {
  const [bodyRef] = useBox(() => ({
    args: scale.map((n) => n),
    position: pos,
    rotation: rot,
    type: "Kinematic",
    material: { friction: 0 },
  }));

  return (
    <mesh ref={bodyRef} material={material} visible={false}>
      <boxBufferGeometry
        attach="geometry"
        args={[scale[0], scale[1], scale[2]]}
      />
    </mesh>
  );
};

const Floor = () => {
  const [bodyRef] = usePlane(() => ({
    rotation: [-Math.PI / 2, 0, 0],
    type: "Kinematic",
    material: { friction: 0 },
  }));

  return (
    <mesh ref={bodyRef} visible={false}>
      <planeBufferGeometry attach="geometry" args={[375, 375]} />
      <meshBasicMaterial attach="material" color="red" />
    </mesh>
  );
};

const Colliders = () => {
  const wallCollidersData = [
    // separador de la mediana
    {
      pos: [55.506, 1, 39.454],
      rot: [0, 0, 0],
      scale: [200, 2, 1],
    },
    // bus cerca del video wall
    // {
    //   pos: [-48.536, 1, 42.259],
    //   rot: [0, 0, 0],
    //   scale: [12, 2, 6.5],
    // },
    // separador calle cerca del bus
    {
      pos: [-23.556, 1, 47.154],
      rot: [0, 0, 0],
      scale: [1, 2, 20],
    },
    // separador área recreación más cerca del bus
    {
      pos: [-46.454, 1, 60.101],
      rot: [0, 0, 0],
      scale: [20, 2, 1],
    },
    // manzana video wall
    {
      pos: [-94.42, 1, 58.228],
      rot: [0, 0, 0],
      scale: [88, 2, 40],
    },
    // coche next to bus cerca del video wall
    // {
    //   pos: [-38.907, 0, 38.797],
    //   rot: [0, 0, 0],
    //   scale: [2.2, 2, 5],
    // },
    // bus between mcdonals and la otra tienda
    {
      pos: [65.924, 1.172, 47.987],
      rot: [0, 0, 0],
      scale: [12, 2, 3.5],
    },
    // separador parking izquierda
    {
      pos: [113.089, 0, 101.962],
      rot: [0, 0, 0],
      scale: [5, 1, 92],
    },
    // parking central
    {
      pos: [65.19, 0, 107.412],
      rot: [0, 0, 0],
      scale: [82, 1, 103],
    },
    // margen izquierda segunda entrada
    {
      pos: [23.939, 0, 101.962],
      rot: [0, 0, 0],
      scale: [5, 1, 92],
    },
    // dead end izuiqerdo pegado al parking
    {
      pos: [121.303, 1, 55.326],
      rot: [0, 0, 0],
      scale: [20, 2, 1],
    },
    // dead end izqueirdo dead end carretera
    {
      pos: [130.882, 1, 47.154],
      rot: [0, 0, 0],
      scale: [1, 2, 20],
    },
    // tiendas norte
    {
      pos: [-165.921, 1, 116.162],
      rot: [0, 0, 0],
      scale: [40, 2, 120],
    },
    // dead end tiendas norte
    {
      pos: [-140.058, 1.434, 72.907],
      rot: [0, 0, 0],
      scale: [20, 2, 1],
    },
    // manzana pasaje y tiendas
    {
      pos: [-94.881, 1, 122.699],
      rot: [0, 0, 0],
      scale: [89, 2, 78],
    },
    // manzana chimeneas y tiendas
    {
      pos: [-20.226, 1, 140.605],
      rot: [0, 0, 0],
      scale: [70, 2, 45],
    },
    // silos
    {
      pos: [-29.537, 1, 105.636],
      rot: [0, 0, 0],
      scale: [30, 2, 38],
    },
    // furgoneta silos
    // {
    //   pos: [-31.11, 1, 86.133],
    //   rot: [0, 0, 0],
    //   scale: [6, 2, 5],
    // },
    // tiendas general
    {
      pos: [-9.928, 1, 173.103],
      rot: [0, 0, 0],
      scale: [300, 2, 5],
    },
    // tiendas norte general
    {
      pos: [-113.025, 1, 170.533],
      rot: [0, 0, 0],
      scale: [75, 2, 5],
    },
    // tiendas norte
    {
      pos: [-99.801, 1, 169.344],
      rot: [0, 0, 0],
      scale: [25, 2, 5],
    },
    // tiendas medio-norte
    {
      pos: [-8.382, 1, 169.665],
      rot: [0, 0, 0],
      scale: [30, 2, 5],
    },
    // tiendas medio-norte esquina
    {
      pos: [-26.26, 1, 170.043],
      rot: [0, MathUtils.DEG2RAD * 30, 0],
      scale: [8, 2, 2],
    },
    // tiendas medio-sur
    {
      pos: [40.423, 1, 170.001],
      rot: [0, 0, 0],
      scale: [30, 2, 5],
    },
    // tiendas sur plaza lado norte
    {
      pos: [107.093, 1, 166.25],
      rot: [0, 0, 0],
      scale: [30, 2, 5],
    },
    // tiendas sur plaza lado izqueirdo
    {
      pos: [117.276, 1, 160.342],
      rot: [0, 0, 0],
      scale: [5, 2, 30],
    },
    // tiendas medio al otro lado
    {
      pos: [39.931, 1, 158.885],
      rot: [0, 0, 0],
      scale: [31.5, 2, 5],
    },
    // tiendas medio norte al otro lado
    {
      pos: [-40, 1, 161.75],
      rot: [0, 0, 0],
      scale: [31.5, 2, 5],
    },
    // valla de entrada a tiendas desde pasaje
    {
      pos: [-144.839, 1, 119.962],
      rot: [0, 0, 0],
      scale: [3, 2, 1],
    },
    // furgoneta en giro
    // {
    //   pos: [-15.076, 1, 88.05],
    //   rot: [0, MathUtils.DEG2RAD * 45, 0],
    //   scale: [6, 2, 6],
    // },
    // separator furgoneta en giro a telefonica, silos
    {
      pos: [-9.201, 1, 104.86],
      rot: [0, MathUtils.DEG2RAD * 23, 0],
      scale: [3, 2, 40],
    },
    // tiendas sur pico 1
    {
      pos: [66.584, 1, 167.952],
      rot: [0, MathUtils.DEG2RAD * 10, 0],
      scale: [31.5, 2, 5],
    },
    // tiendas sur pico 2
    {
      pos: [86.723, 1, 165.674],
      rot: [0, MathUtils.DEG2RAD * -5, 0],
      scale: [11, 2, 5],
    },
    // recreación arboles y sombrillas 1
    {
      pos: [13.789, 0, 95.86],
      rot: [0, 0, 0],
      scale: [2.2, 2, 32],
    },
    // recreación arboles y sombrillas 2
    {
      pos: [8.119, 0, 100.138],
      rot: [0, MathUtils.DEG2RAD * 23, 0],
      scale: [2.2, 2, 23],
    },
    // recreación arboles y sombrillas 3
    {
      pos: [8.554, 0, 85.339],
      rot: [0, MathUtils.DEG2RAD * -45, 0],
      scale: [2.2, 2, 15],
    },
    // piramide
    {
      pos: [-7.726, 0, 77.924],
      rot: [0, MathUtils.DEG2RAD * -20.2, 0],
      scale: [6, 2, 6],
    },
    // recreación pirámide cubo
    {
      pos: [8.598, 0, 61.444],
      rot: [0, 0, 0],
      scale: [12, 2, 12],
    },

    // recreación pirámide ala izquierda top
    {
      pos: [6.21, 0, 71.683],
      rot: [0, MathUtils.DEG2RAD * 30, 0],
      scale: [2, 2, 11],
    },
    // recreación pirámide ala izquierda left
    {
      pos: [11.394, 0, 71.119],
      rot: [0, MathUtils.DEG2RAD * -29, 0],
      scale: [2, 2, 9.5],
    },
    // recreación pirámide ala derecha top
    {
      pos: [-0.433, 0, 63.549],
      rot: [0, MathUtils.DEG2RAD * 50, 0],
      scale: [2, 2, 11],
    },
    // recreación pirámide ala derecha right
    {
      pos: [-0.587, 0, 58.581],
      rot: [0, MathUtils.DEG2RAD * -60, 0],
      scale: [2, 2, 9],
    },
    // recreación plaza
    {
      pos: [],
      rot: [0],
      scale: [],
    },
    // recreación plaza area pequeña 1
    {
      pos: [-36.66, 0, 57.623],
      rot: [0, 0, 0],
      scale: [13, 2, 8],
    },
    // recreación plaza area pequeña 2
    {
      pos: [-26.733, 0, 59.084],
      rot: [0, MathUtils.DEG2RAD * -45, 0],
      scale: [3, 2, 10],
    },
    // recreación plaza area pequeña 3
    {
      pos: [-27.618, 0, 54.606],
      rot: [0, MathUtils.DEG2RAD * -45, 0],
      scale: [7, 2, 2],
    },
    // recreación plaza area grande 1
    {
      pos: [-14.649, 0, 64.794],
      rot: [0, MathUtils.DEG2RAD * 69, 0],
      scale: [20, 2, 2],
    },
    // recreación plaza area grande 2
    {
      pos: [-21.99, 0, 71.746],
      rot: [0, MathUtils.DEG2RAD * -30, 0],
      scale: [11, 2, 2],
    },
    // recreación plaza area grande 3
    {
      pos: [-27.404, 0, 67.852],
      rot: [0, MathUtils.DEG2RAD * 18, 0],
      scale: [1, 2, 4],
    },
    // recreación plaza area grande 4
    {
      pos: [-19.754, 0, 61.114],
      rot: [0, MathUtils.DEG2RAD * 35, 0],
      scale: [20, 2, 2],
    },
    // fuente plaza 1
    {
      pos: [-36.66, 0, 72.272],
      rot: [0, 0, 0],
      scale: [8, 2, 8],
    },
    // fuente plaza 2
    {
      pos: [-36.66, 0, 72.046],
      rot: [0, 0, 0],
      scale: [6, 2, 12],
    },

    // Food Truck
    {
      pos: [-8.018, 1.089, 87.702],
      rot: [0, 20, 0],
      scale: [3.445, 3.115, 5.172],
    },
  ];

  const wallMaterial = useMemo(() => {
    return new MeshBasicMaterial({ color: "green" });
  }, []);

  return (
    <>
      {wallCollidersData.map((c, i) => (
        <Wall key={`wall-${i}`} {...c} material={wallMaterial} />
      ))}

      <Floor />
    </>
  );
};

export default Colliders;
